import React from "react";
import { Link } from "react-router-dom";
import { styled } from '@mui/material/styles';
import pxToRem from "libs/pxToRem";

import MuiList from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MuiListItemIcon from "@mui/material/ListItemIcon";
import MuiListItemText from "@mui/material/ListItemText";

import NavbarLayout from "components/styled/layouts/navbarLayout/NavbarLayout";
import TopbarWithTitle from "components/styled/layouts/navbarLayout/TopbarWithTitle";
import ContentSectionMaxWidth from "components/styled/contentSection/ContentSectionMaxWidth";

import { useAuth } from "components/app/auth/useAuth";

import Divider from "@mui/material/Divider";
import MuiUsuarioIcon from "@mui/icons-material/AccountCircle";
import MuiActiveIcon from "@mui/icons-material/ChevronRight";
import VersionIcon from "@mui/icons-material/ChangeHistory";
import MuiCerrarSesionIcon from "@mui/icons-material/ExitToApp";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";

import environment from "environment";
import DevInfo from "components/business/DevInfo";

const socials: { [key: string]: string } = {
  facebook: "Facebook",
  "google-oauth2": "Gmail",
  email: "Email",
  twitter: "Twitter",
  windowslive: "Hotmail",
  yahoo: "Yahoo",
};

const Info: React.FC = () => {
  const { session } = useAuth();

  const socialNetworkId = session?.user?.socialNetworkId
    ? session?.user?.socialNetworkId
    : "";

  const email = session?.user?.email ? session?.user?.email : "";

  return (
    <NavbarLayout
      topbar={() => <TopbarWithTitle title={"Información"} maxWidth />}
    >
      <ContentSectionMaxWidth>
        <List>
          <LightTooltip
            title={email}
            placement={"top"}
            className="tooltipCustom"
          >
            <ListItem>
              <ListItemIcon>
                {!session?.user?.picture && <UsuarioIcon />}
                {session?.user?.picture && (
                  <Avatar
                    alt="perfil"
                    src={session?.user?.picture}
                    style={{ height: 48, width: 48 }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={session?.user?.preferred_username}
                secondary={
                  socials[socialNetworkId]
                    ? `${socials[socialNetworkId]} | ${email}`
                    : email
                }
              />
            </ListItem>
          </LightTooltip>
          <Divider />
          <Divider />
          <ListItemLink
            to={"/signout"}
            style={{ borderRadius: `0 0 12px 12px` }}
          >
            <ListItemIcon>
              <CerrarSesionIcon />
            </ListItemIcon>
            <ListItemText primary="Cerrar sesión" />
            <ListItemSecondaryAction>
              <ActiveIcon />
            </ListItemSecondaryAction>
          </ListItemLink>
          <Divider />
          <ListItem sx={{ opacity: 0.38 }}>
            <ListItemIcon>
              <VersionIcon />
            </ListItemIcon>
            <ListItemText
              primary={`Version ${environment.versionDescription}`}
            />
          </ListItem>
        </List>

        <DevInfo />
      </ContentSectionMaxWidth>
    </NavbarLayout>
  );
};

export default Info;

const ListItemLink = (props: any) => {
  return <ListItem button component={Link} {...props} />;
};

const ListContainer = (props: any) => {
  return <MuiList component={"nav"} {...props} />;
};

const List = styled(ListContainer)(({ theme }) => ({
  width: "100%",
  boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.16)",
  borderRadius: pxToRem(12),
  backgroundColor: theme.palette.background.paper,
  padding: 0,
}));

const ListItemText = styled(MuiListItemText)({
  "& .MuiListItemText-primary": {
    fontWeight: 600,
  },
  "& .MuiListItemText-secondary": {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
});

const ListItemIcon = styled(MuiListItemIcon)({
  minWidth: 0,
  paddingRight: 16,
});

const UsuarioIcon = styled(MuiUsuarioIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: 48,
  width: 48,
}));

const CerrarSesionIcon = styled(MuiCerrarSesionIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const ActiveIcon = styled(MuiActiveIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginTop: 3,
}));

const LightTooltip = styled(({ className, ...props }: { className?: string } & React.ComponentProps<typeof Tooltip>) => (
  <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
  "&.tooltipCustom": {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
    fontWeight: "bold",
  },
}));
